import * as React from 'react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';

const sx = {
  container: {
    height: '100%',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },
  barcontainer: {
    position: 'relative',
    transform: 'translateY(-50%)',
    top: '50%',
    width: '64px',
    height: '100%',
    float: 'left',
    borderRadius: '5px 5px 0px 0px',
  },
  bar: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    boxSizing: 'border-box',
    animation: '$grow 1.5s ease-out forwards',
    transformOrigin: 'bottom',
    borderRadius: '5px 5px 0px 0px',
    display: 'flex',
    justifyContent: 'center',
  },
  '@keyframes grow': {
    from: {
      transform: 'scaleY(0)',
    },
  },
  totalTag: {
    position: 'absolute',
    fontSize: '11px',
    fontWeight: '550'
  },
  yAxis: {
    transform: 'rotate(-90deg)',
    left: '-35px',
    position: 'absolute'
  }
};

// Low to high. Used positionally when the caller supplies bands, so any set of
// score ranges colours correctly without needing a matching arm below.
const BAND_COLORS = ['#C51407', '#F5B847', '#00664A'];

// Function to get color based on range
const getColorByRange = (range) => {
  // Handle coding mock ranges (1.2-2.33, 2.34-3.67, 3.68-5.0)
  if (range.includes('1.2-2.33')) return '#C51407';
  if (range.includes('2.34-3.67')) return '#F5B847';
  if (range.includes('3.68-5.0')) return '#00664A';

  // Handle regular percentage ranges (0-30, 31-60, 61-100)
  if (range.includes('0-30')) return '#C51407';
  if (range.includes('31-60')) return '#F5B847';
  if (range.includes('61-100')) return '#00664A';

  // Default fallback
  return '#C51407';
};

// `color` is the legend swatch and is deliberately NOT used for the bar itself —
// the verbal/coding charts have always derived the bar colour from the range
// string. `barColor` is the opt-in override the band-driven charts pass.
export const BarContainer = ({ height, color, total, range, barColor: overrideColor }) => {
  const barColor = overrideColor || getColorByRange(range);

  return (
    <Box sx={sx.barcontainer}>
      <Box
        sx={sx.bar}
        style={{
          height: `${height}%`,
          backgroundColor: barColor,
        }}
      >
        <Typography sx={{...sx.totalTag, top: height === 100 ? '2px' : '-15px',}}>
          { total > 0 && total }
        </Typography>
      </Box>
    </Box>
  );
};

const VerticalGraph = ({
  averageScoreStats, averageScoreLegends, xAsix, yAsix, totalStudents, bands
}) => {

  // Two ways in. `bands` is the payload-driven form — an ordered
  // [{ key, label, count }] straight from the API, so the labels and the counts
  // come from one source and cannot disagree. Without it we keep the original
  // behaviour: legends declared client-side, counts looked up by range string.
  const series = bands?.length
    ? bands.map((band, index) => ({
      label: band.label,
      count: band.count || 0,
      barColor: BAND_COLORS[index % BAND_COLORS.length],
    }))
    : (averageScoreLegends || []).map((legend) => ({
      label: legend.range,
      count: averageScoreStats?.[legend.range],
      swatch: legend.color,
    }));

  return (
    <Box display='flex' flexDirection='column' gap='15px' flex='1 1 240px' minWidth={0}>
      {/* The y-axis caption sits in the flow as a vertical-writing-mode block.
          It used to be absolutely positioned at left:-74, which meant it hung
          outside the chart and relied on a wide gutter to avoid landing on the
          dial beside it. */}
      <Box display='flex' alignItems='stretch' gap='4px' minWidth={0}>
        <Typography
          variant='body01-bold'
          color='neutral.clr-600'
          component="div"
          sx={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            textAlign: 'center',
            flexShrink: 0,
            alignSelf: 'center',
          }}
        >
          {yAsix}
        </Typography>
      <Box display="flex" alignItems="center" height='143px' flex='1 1 auto' minWidth={0}
        paddingLeft='24px' borderLeft= "1px solid #8692A3" borderBottom="1px solid #8692A3"
      >
                 <Box sx={sx.container} width='100%'>
           {
             series.map((data,index)=>{
               return (
                 <Box key={index}>
                   <BarContainer
                     height={(data.count/(totalStudents))*100 || 0}
                     color={data.swatch}
                     barColor={data.barColor}
                     total={data.count}
                     range={data.label}
                   /><br/>
                   <Typography variant='body02-semiBold'>
                     { data?.label }
                   </Typography>
                 </Box>
               )
             })
           }
         </Box>
      </Box>
      </Box>
      <Box textAlign='center' mt={2} >
        <Typography variant='body01-bold' color='neutral.clr-600'>
          { xAsix }
        </Typography>
      </Box>
    </Box>
  );
};

export default VerticalGraph;
